import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import CTA from '../home/components/CTA';
import { useState, useEffect } from 'react';
import Breadcrumb from '../../components/Breadcrumb';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export default function AboutPage() {
  const [isQuoteFormOpen, setIsQuoteFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    // Validate textarea length
    const projectDetails = formData.get('project_details') as string;
    if (projectDetails && projectDetails.length > 500) {
      alert('Project details must be 500 characters or less');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d5j65k1845a7l1lrhaf0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setShowSuccess(true);
        form.reset();
        setTimeout(() => {
          setShowSuccess(false);
          setIsQuoteFormOpen(false);
        }, 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Page title
    document.title = 'About Yuma Spray Foam Pros | Local Insulation Experts';

    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Learn about Yuma Spray Foam Pros. 20+ years serving Arizona with expert insulation services. Licensed, insured, and trusted by 500+ customers. Call (928) 427-8140.'
      );
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/about');

    // Geo-location tags
    const metaTags = [
      { name: 'geo.region', content: 'US-AZ' },
      { name: 'geo.placename', content: 'Yuma' },
      { name: 'geo.position', content: '32.6927;-114.6277' },
      { name: 'ICBM', content: '32.6927, -114.6277' },
    ];
    metaTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'About Yuma Spray Foam Pros | Local Insulation Experts' },
      {
        property: 'og:description',
        content: 'Learn about Yuma Spray Foam Pros. 20+ years serving Arizona with expert insulation services.'
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.origin + '/about' },
      {
        property: 'og:image',
        content:
          'https://public.readdy.ai/ai/img_res/f1cbe7662e932c2914be25a39ffa882c.jpg'
      }
    ];
    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'About Yuma Spray Foam Pros | Local Insulation Experts' },
      {
        name: 'twitter:description',
        content: '20+ years serving Arizona with expert spray foam insulation services. Licensed & insured.'
      },
      {
        name: 'twitter:image',
        content:
          'https://readdy.ai/api/search-image?query=Professional%20insulation%20contractor%20team%20with%20modern%20equipment%20and%20safety%20gear%20standing%20confidently%20in%20front%20of%20service%20vehicle%20showcasing%20expertise%20and%20reliability%20in%20spray%20foam%20insulation%20industry&width=1200&height=630&seq=twitter-about-001&orientation=landscape'
      }
    ];
    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Last-modified tag
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    // Breadcrumb schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: window.location.origin + '/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'About',
          item: window.location.origin + '/about'
        }
      ]
    };

    let breadcrumbScript = document.querySelector('script[data-schema="breadcrumb-about"]');
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.setAttribute('type', 'application/ld+json');
      breadcrumbScript.setAttribute('data-schema', 'breadcrumb-about');
      document.head.appendChild(breadcrumbScript);
    }
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);

    // LocalBusiness schema
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Yuma Spray Foam Pros',
      telephone: '(928) 427-8140',
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 32.6927,
        longitude: -114.6277
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        reviewCount: '127'
      }
    };

    let businessScript = document.querySelector('script[data-schema="business-about"]');
    if (!businessScript) {
      businessScript = document.createElement('script');
      businessScript.setAttribute('type', 'application/ld+json');
      businessScript.setAttribute('data-schema', 'business-about');
      document.head.appendChild(businessScript);
    }
    businessScript.textContent = JSON.stringify(localBusinessSchema);
  }, []);

  useScrollReveal();

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <Breadcrumb 
        items={[
          { label: 'Home', href: '/' },
          { label: 'About Us' }
        ]}
      />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-scroll-reveal>
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            About Yuma Spray Foam Pros
          </h1>
          <p className="text-xl text-gray-600">
            Your trusted insulation specialists serving the region for over 20 years
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none" data-scroll-reveal>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Since 2001, we've been the premier <a href="/insulation-services-arizona" className="text-blue-600 hover:text-blue-700 font-semibold underline decoration-2 underline-offset-2 transition-colors duration-300">insulation contractor</a> in <strong>Yuma, Arizona</strong>, dedicated to helping homeowners and businesses achieve maximum energy efficiency and comfort with the latest insulation technology. Our locally-owned company was founded on the principle that quality work shouldn't be a luxury—it's a necessity in the extreme <strong>desert climate</strong>.
            </p>
            
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              What started as a small operation has grown into the area's most trusted contractor, completing over 500 successful projects throughout the community. Our team of <strong>certified professionals</strong> brings unmatched expertise in <a href="/closed-cell-spray-foam" className="text-blue-600 hover:text-blue-700 font-semibold underline decoration-2 underline-offset-2 transition-colors duration-300">spray foam application</a>, using only <strong>premium materials</strong> specifically chosen for their performance in harsh desert conditions. We understand that every property is unique, which is why we provide personalized solutions tailored to your specific needs and budget.
            </p>

            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Our commitment goes beyond just installation. We believe in building lasting relationships with our clients through <strong>exceptional service</strong>, transparent pricing, and guaranteed results. Whether you need home insulation or <a href="/commercial-insulation-yuma" className="text-blue-600 hover:text-blue-700 font-semibold underline decoration-2 underline-offset-2 transition-colors duration-300">commercial insulation solutions</a>, you're choosing a partner who genuinely cares about your comfort, <strong>energy savings</strong>, and long-term satisfaction.
            </p>

            <div className="grid md:grid-cols-3 gap-8 mt-12" data-scroll-reveal>
              <div className="text-center p-6 bg-blue-50 rounded-xl hover:scale-105 hover:shadow-lg transition-all duration-300 cursor-pointer">
                <div className="text-5xl font-bold text-blue-600 mb-2">24+</div>
                <div className="text-gray-700 font-semibold">Years of Experience</div>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-xl hover:scale-105 hover:shadow-lg transition-all duration-300 cursor-pointer">
                <div className="text-5xl font-bold text-blue-600 mb-2">500+</div>
                <div className="text-gray-700 font-semibold">Projects Completed</div>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-xl hover:scale-105 hover:shadow-lg transition-all duration-300 cursor-pointer">
                <div className="text-5xl font-bold text-blue-600 mb-2">100%</div>
                <div className="text-gray-700 font-semibold">Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Services Section - NEW */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12" data-scroll-reveal>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Explore Our Services
            </h2>
            <p className="text-lg text-gray-600">
              Discover the comprehensive insulation solutions we offer throughout Arizona
            </p>
          </div>

          {/* Mobile: Horizontal Scroll */}
          <div className="md:hidden relative">
            <div className="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
              <div className="flex gap-4" style={{ width: 'max-content' }}>
                <a 
                  href="/residential-insulation-yuma" 
                  className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 transition-all duration-300 cursor-pointer group"
                  style={{ width: '280px', flexShrink: 0 }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:rotate-12 transition-all duration-300">
                    <i className="ri-home-8-line text-2xl text-blue-600 group-hover:text-white transition-colors duration-300"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                    Residential Insulation
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Cut cooling costs up to 50% with professional home insulation services
                  </p>
                </a>

                <a 
                  href="/commercial-insulation-yuma" 
                  className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 transition-all duration-300 cursor-pointer group"
                  style={{ width: '280px', flexShrink: 0 }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:rotate-12 transition-all duration-300">
                    <i className="ri-building-line text-2xl text-blue-600 group-hover:text-white transition-colors duration-300"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                    Commercial Insulation
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Reduce business energy costs 30-50% with large-scale solutions
                  </p>
                </a>

                <a 
                  href="/insulation-services-arizona" 
                  className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 transition-all duration-300 cursor-pointer group"
                  style={{ width: '280px', flexShrink: 0 }}
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:rotate-12 transition-all duration-300">
                    <i className="ri-tools-line text-2xl text-blue-600 group-hover:text-white transition-colors duration-300"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                    All Services
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Explore our complete range of spray foam insulation solutions
                  </p>
                </a>
              </div>
            </div>
            <div className="text-center text-sm text-gray-500 mt-2">
              Swipe to see more →
            </div>
          </div>

          {/* Desktop: Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-6" data-scroll-reveal>
            <a 
              href="/residential-insulation-yuma" 
              className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 hover:scale-105 hover:shadow-lg transition-all duration-300 cursor-pointer group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:rotate-12 transition-all duration-300">
                <i className="ri-home-8-line text-2xl text-blue-600 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                Residential Insulation
              </h3>
              <p className="text-gray-600 text-sm">
                Cut cooling costs up to 50% with professional home insulation services
              </p>
            </a>

            <a 
              href="/commercial-insulation-yuma" 
              className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 hover:scale-105 hover:shadow-lg transition-all duration-300 cursor-pointer group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:rotate-12 transition-all duration-300">
                <i className="ri-building-line text-2xl text-blue-600 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                Commercial Insulation
              </h3>
              <p className="text-gray-600 text-sm">
                Reduce business energy costs 30-50% with large-scale solutions
              </p>
            </a>

            <a 
              href="/insulation-services-arizona" 
              className="block bg-gray-50 p-6 rounded-xl hover:bg-blue-50 hover:scale-105 hover:shadow-lg transition-all duration-300 cursor-pointer group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-600 group-hover:rotate-12 transition-all duration-300">
                <i className="ri-tools-line text-2xl text-blue-600 group-hover:text-white transition-colors duration-300"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">
                All Services
              </h3>
              <p className="text-gray-600 text-sm">
                Explore our complete range of spray foam insulation solutions
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section with Quote Button */}
      <section className="relative py-20 bg-[#1A2332]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-scroll-reveal>
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Transform Your Property?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Get a free, no-obligation quote today and discover how much you can save with professional spray foam insulation in Yuma, AZ.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setIsQuoteFormOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 hover:scale-105 hover:shadow-xl text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg group"
            >
              Request Free Quote
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
            </button>
          </div>
        </div>
      </section>

      <Footer />

      {/* Quote Form Modal */}
      {isQuoteFormOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-scaleIn">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-bold">Request Your Free Quote</h3>
                <p className="text-blue-100 mt-1">We'll get back to you within 24 hours</p>
              </div>
              <button
                onClick={() => setIsQuoteFormOpen(false)}
                className="text-white hover:bg-white/20 hover:rotate-90 rounded-full w-10 h-10 flex items-center justify-center transition-all duration-300 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-6">
              {showSuccess ? (
                <div className="text-center py-12 animate-slideUp">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600">Your quote request has been received. We'll get back to you within 24 hours with a detailed estimate.</p>
                </div>
              ) : (
                <form id="about-quote-form" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:scale-105 text-sm transition-all duration-300"
                        placeholder="John Smith"
                      />
                    </div>
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:scale-105 text-sm transition-all duration-300"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:scale-105 text-sm transition-all duration-300"
                        placeholder="(928) 427-8140"
                      />
                    </div>
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                        Property Type *
                      </label>
                      <div className="relative">
                        <select
                          name="property_type"
                          required
                          className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:scale-105 text-sm cursor-pointer transition-all duration-300 appearance-none bg-white"
                        >
                          <option value="">Select type</option>
                          <option value="Residential">Residential</option>
                          <option value="Commercial">Commercial</option>
                          <option value="Industrial">Industrial</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-500 text-xl transition-transform duration-300 group-focus-within:text-blue-600 group-focus-within:rotate-180"></i>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                        Service Needed *
                      </label>
                      <div className="relative">
                        <select
                          name="project_type"
                          required
                          className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:scale-105 text-sm cursor-pointer transition-all duration-300 appearance-none bg-white"
                        >
                          <option value="">Select service</option>
                          <option value="Attic Insulation">Attic Insulation</option>
                          <option value="Wall Insulation">Wall Insulation</option>
                          <option value="Open Cell Spray Foam">Open Cell Spray Foam</option>
                          <option value="Closed Cell Spray Foam">Closed Cell Spray Foam</option>
                          <option value="Full Property Insulation">Full Property Insulation</option>
                          <option value="Other">Other</option>
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <i className="ri-arrow-down-s-line text-gray-500 text-xl transition-transform duration-300 group-focus-within:text-blue-600 group-focus-within:rotate-180"></i>
                        </div>
                      </div>
                    </div>

                    <div className="group">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                        Square Footage (Optional)
                      </label>
                      <input
                        type="text"
                        name="square_footage"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:scale-105 text-sm transition-all duration-300"
                        placeholder="e.g., 2000 sq ft"
                      />
                    </div>
                  </div>

                  <div className="group">
                    <label className="block text-sm font-semibold text-gray-700 mb-2 transition-colors duration-300 group-focus-within:text-blue-600">
                      Project Details
                    </label>
                    <textarea
                      name="project_details"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:scale-105 text-sm resize-none transition-all duration-300"
                      placeholder="Tell us about your insulation needs..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-emerald-600 text-white py-4 rounded-lg font-semibold hover:bg-emerald-700 hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl group"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        Submit Quote Request
                        <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-300 inline-block"></i>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
